import {css, StyleSheet} from 'aphrodite';
import React from 'react';
import {Handle, NodeProps, Position} from 'reactflow';

import {
  INTERFACE_ICONS,
  SchemaDataForDiagram,
  SCHEMA_COLORS,
  SCHEMA_BACKGROUND_COLORS,
} from 'schemas/data-model-diagram/SchemaDiagramConstants';
import {SchemaDomainTypeInterface} from 'schemas/data-model-diagram/types';
import {BLUE_LIGHT, GRAY, WHITE} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import * as logging from 'util/Logging';

function getColors(domainTypeInterface: string): [string, string] {
  const headerColor = SCHEMA_COLORS[domainTypeInterface as SchemaDomainTypeInterface] ?? WHITE;
  const tableColor = SCHEMA_BACKGROUND_COLORS[domainTypeInterface as SchemaDomainTypeInterface] ?? '#EFEFEF';
  if (headerColor === WHITE) {
    logging.warn(
      `domainTypeInterface ${domainTypeInterface} has no assigned color in the Data Model Diagram`
    );
  }
  return [headerColor, tableColor];
}

export default function SchemaNode(props: NodeProps<SchemaDataForDiagram>): JSX.Element {
  const [headerColor, tableColor] = getColors(props.data.domainTypeInterface);
  // TODO: Override icons with the schema's chosen icon. Requires either adding that to the
  // QueryApiIntrospection or fetching it separately.
  const maybeIcon = INTERFACE_ICONS[props.data.domainTypeInterface as SchemaDomainTypeInterface];
  return (
    <>
      {props.data.relationships.targetRelationships.map((rel) => (
        <Handle type="target" position={Position.Left} key={`${props.data.id}-target-${rel.id.toString()}`} />
      ))}
      <div
        key={props.data.id}
        className={css(
          styles.schemaNode,
          props.data.highlight && styles.highlight,
          props.data.fadeout && styles.fadeout
        )}
      >
        <div className={css(styles.title, styles.section)} style={{backgroundColor: headerColor}}>
          {maybeIcon && <BenchlingIcon className={css(styles.titleIcon)} icon={maybeIcon} />}
          {props.data.label}
        </div>
        <div className={css(styles.tableContainer)} style={{backgroundColor: tableColor}}>
          <table className={css(styles.table, styles.section)}>
            <thead>
              <tr>
                <th />
                <th>Name</th>
                <th>Multi</th>
              </tr>
            </thead>
            <tbody>
              {props.data.fields.map((field) => (
                <tr key={field.name} className={css(styles.row)}>
                  <td className={css(styles.icon)}>
                    <BenchlingIcon icon={field.iconName} />
                  </td>
                  <td>{field.name}</td>
                  <td>{field.isMulti ? 'Yes' : 'No'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {props.data.relationships.sourceRelationships.map((rel) => (
        <Handle
          type="source"
          position={Position.Right}
          key={`${props.data.id}-source-${rel.id.toString()}`}
        />
      ))}
    </>
  );
}

const styles = StyleSheet.create({
  schemaNode: {
    border: '1px solid',
    borderColor: GRAY,
    borderRadius: '4px',
    cursor: 'grab',
    transition: 'opacity 0.2s',
  },
  section: {
    paddingLeft: '8px',
    paddingRight: '8px',
  },
  icon: {
    paddingRight: '2px',
  },
  row: {
    padding: '2px 0',
  },
  title: {
    fontSize: '12px',
    paddingTop: '4px',
    paddingBottom: '4px',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
  },
  titleIcon: {
    paddingRight: '6px',
    height: '16px',
    width: '16px',
  },
  tableContainer: {
    width: '100%',
    padding: '4px',
  },
  table: {
    width: '100%',
    fontSize: '10px',
  },
  highlight: {
    border: '2px solid',
    boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
    borderColor: BLUE_LIGHT,
  },
  fadeout: {
    opacity: 0.2,
  },
});
